import {
  Card,
  DataStatus,
  IconButton,
  isFile,
  OnBeforeUpload,
  openDeleteDialog,
  showNotification,
  Upload,
  UploadState,
} from 'platform/components';
import {HStack, Icon, Link, Show, Space, Spinner, Text, VStack} from 'platform/foundation';
import {match} from 'ts-pattern';

import {useState} from 'react';

import {inc, isNil, isNotNil} from 'ramda';
import {isArray} from 'ramda-adjunct';

import {
  useGetInspectionEquipmentDocumentQuery,
  useRemoveInspectionEquipmentDocumentMutation,
} from '@dms/api/core';
import {getApiErrorMessages} from '@dms/api/utils';
import i18n from '@dms/i18n';
import {handleApiError} from '@dms/shared';

import {ApiError, suffixTestId, TestIdProps} from 'shared/web';

import {EQUIPMENT_DOCUMENT_FILE_CONSTRAINTS} from '../../../constants/equipmentDocumentFileConstraints';
import {useEquipmentDocumentUpload} from '../../../hooks/useEquipmentDocumentUpload';
import {hasPdfSignature} from '../../../utils/hasPdfSignature';
import {
  EquipmentDocumentFileError,
  validateEquipmentDocumentFile,
} from '../../../utils/validateEquipmentDocumentFile';

/**
 * The signed `downloadUrl` expires after four hours; refetching hourly keeps the link of a card that
 * stays open valid.
 */
const DOWNLOAD_URL_REFRESH_INTERVAL_MS = 60 * 60 * 1000;

interface EquipmentDocumentCardContentProps extends TestIdProps {
  auditId: string;
  canUpdate: boolean;
}

export function EquipmentDocumentCardContent(props: EquipmentDocumentCardContentProps) {
  const equipmentDocumentQuery = useGetInspectionEquipmentDocumentQuery(
    {auditId: props.auditId},
    {pollingInterval: DOWNLOAD_URL_REFRESH_INTERVAL_MS}
  );
  const [removeEquipmentDocument, {isLoading: isRemoving}] =
    useRemoveInspectionEquipmentDocumentMutation();
  const {uploadEquipmentDocument, phase} = useEquipmentDocumentUpload(props.auditId);
  const [fileError, setFileError] = useState<EquipmentDocumentFileError | null>(null);
  // Remounting `Upload` after every settled request drops its internal success/error look, which it
  // would otherwise keep whenever the request settles before the `uploadState` prop changes.
  const [uploadControlKey, setUploadControlKey] = useState(0);

  const equipmentDocument = equipmentDocumentQuery.data?.equipmentDocument;
  const isUploading = phase !== 'idle';
  const isMutationPending = isUploading || isRemoving;

  /**
   * Resolving to `false` makes rc-upload drop the file before `onStart`, so the button never enters
   * its uploading state for an ineligible file.
   */
  const rejectIneligibleFile: OnBeforeUpload = (file) => {
    const error = validateEquipmentDocumentFile(file);
    setFileError(error);

    if (isNotNil(error)) {
      return false;
    }

    return hasPdfSignature(file).then((isPdf) => {
      setFileError(isPdf ? null : 'notPdf');
      return isPdf;
    });
  };

  const handleRemove = () =>
    openDeleteDialog({
      text: i18n.t('entity.inspection.labels.removeEquipmentDocumentConfirmation'),
      onConfirm: () =>
        removeEquipmentDocument({auditId: props.auditId})
          .unwrap()
          .then(() => showNotification.success(i18n.t('general.notifications.successfullyDeleted')))
          .catch(handleApiError),
      'data-testid': suffixTestId('equipmentDocument-removeDialog', props),
    });

  const uploadControl = (
    <Upload
      key={uploadControlKey}
      type="button"
      accept={`${EQUIPMENT_DOCUMENT_FILE_CONSTRAINTS.mimeType},${EQUIPMENT_DOCUMENT_FILE_CONSTRAINTS.extension}`}
      buttonVariant="secondary"
      uploadIcon="file/upload"
      uploadText={i18n.t(
        isNotNil(equipmentDocument) ? 'general.actions.replace' : 'general.actions.upload'
      )}
      uploadingText={i18n.t('general.labels.uploading')}
      uploadState={isUploading ? UploadState.Uploading : UploadState.Idle}
      isDisabled={isMutationPending}
      beforeUpload={rejectIneligibleFile}
      // `Upload` enters its own uploading state on start and only leaves it when told the request
      // settled, so every path through here has to settle it.
      customRequest={(option) => {
        if (!isFile(option.file)) {
          option.onError?.(new Error('Equipment document is not a file'));
          setUploadControlKey(inc);
          return;
        }
        void uploadEquipmentDocument(option.file).then((isAttached) => {
          if (isAttached) {
            option.onSuccess?.(null);
          } else {
            option.onError?.(new Error('Equipment document was not attached'));
          }
          setUploadControlKey(inc);
        });
      }}
      data-testid={suffixTestId('equipmentDocument-upload', props)}
    />
  );

  return (
    <Card
      title={i18n.t('entity.inspection.labels.equipmentDocument')}
      variant="inlineWhite"
      data-testid={suffixTestId('equipmentDocument', props)}
    >
      <DataStatus
        isLoading={equipmentDocumentQuery.isLoading}
        isError={equipmentDocumentQuery.isError}
        errorMessage={getErrorMessage(equipmentDocumentQuery.error)}
        errorAction={{
          title: i18n.t('general.actions.tryAgain'),
          onClick: equipmentDocumentQuery.refetch,
        }}
        minHeight={12}
      >
        <VStack spacing={3}>
          {isNotNil(equipmentDocument) ? (
            <HStack spacing={3} align="center">
              <Icon value="files_default/file_type_PDF" size={8} />
              <Link
                size="small"
                href={equipmentDocument.downloadUrl}
                target="_blank"
                rel="noreferrer"
                title={equipmentDocument.fileName}
                data-testid={suffixTestId('equipmentDocument-download', props)}
              />
              <Space fillAvailable />
              <Show when={props.canUpdate}>
                {uploadControl}
                <IconButton
                  icon="action/delete"
                  severity="danger"
                  aria-label={i18n.t('general.actions.remove')}
                  isDisabled={isMutationPending}
                  onClick={handleRemove}
                  data-testid={suffixTestId('equipmentDocument-remove', props)}
                />
              </Show>
            </HStack>
          ) : (
            <HStack spacing={3} align="center">
              <Text size="small" color="tertiary">
                {i18n.t('entity.inspection.labels.noEquipmentDocument')}
              </Text>
              <Space fillAvailable />
              <Show when={props.canUpdate}>{uploadControl}</Show>
            </HStack>
          )}
          <Show when={isNotNil(fileError)}>
            <Text
              size="xSmall"
              color="danger"
              data-testid={suffixTestId('equipmentDocument-fileError', props)}
            >
              {getFileErrorMessage(fileError)}
            </Text>
          </Show>
          {/* The upload button already reads "Uploading"; only the attach step needs its own label. */}
          <Show when={phase === 'attaching'}>
            <HStack spacing={2} align="center">
              <Spinner size="small" />
              <Text
                size="xSmall"
                color="tertiary"
                data-testid={suffixTestId('equipmentDocument-uploadPhase', props)}
              >
                {i18n.t('entity.inspection.labels.equipmentDocumentAttaching')}
              </Text>
            </HStack>
          </Show>
        </VStack>
      </DataStatus>
    </Card>
  );
}

const getFileErrorMessage = (fileError: EquipmentDocumentFileError | null) =>
  match(fileError)
    .with('notPdf', () => i18n.t('entity.inspection.errors.invalidFileType'))
    .with('invalidSize', () => i18n.t('entity.inspection.errors.equipmentDocumentInvalidSize'))
    .with(null, () => null)
    .exhaustive();

/**
 * A 403 or 404 must read as a failure, never as "no attachment"; the backend's own message
 * (e.g. access denied vs. missing inspection) is the most specific one available.
 */
const getErrorMessage = (error: ApiError | undefined) => {
  if (isNil(error)) {
    return undefined;
  }

  const messages = getApiErrorMessages(error);

  return isArray(messages) ? messages.join(' ') : messages;
};
